import { useEffect, useState, useCallback } from 'react';
import { useAppStore } from '../../store/appStore';
import { GmailConnection, GmailEmail } from 'api-types';
import { Mail, ArrowLeft, AlertCircle } from 'lucide-react';
import { Typography } from '@mui/joy';

type ViewState = 'loading' | 'login' | 'connected' | 'error';

export default function EmailAgentHome() {
  const [viewState, setViewState] = useState<ViewState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<GmailConnection | null>(null);
  const [emails, setEmails] = useState<GmailEmail[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<GmailEmail | null>(null);
  const [showProfileMenu, setShowProfileMenu] = useState<boolean>(false);

  const {
    getGmailConnectionStatus,
    connectGmailAccount,
    disconnectGmailAccount,
    fetchGmailEmails,
  } = useAppStore();

  const checkConnectionStatus = useCallback(async () => {
    try {
      setError(null);
      const response = await getGmailConnectionStatus();

      if (response.connected && response.connection?.status === 'ACTIVE') {
        setConnection(response.connection as GmailConnection);
        setViewState('connected');
        return true;
      } else {
        setConnection(null);
        setViewState('login');
        return false;
      }
    } catch (err) {
      console.error('Failed to check connection status:', err);
      setError('Failed to check Gmail connection status. Please try again.');
      setViewState('error');
      return false;
    }
  }, [getGmailConnectionStatus]);

  const loadEmails = useCallback(async () => {
    if (viewState !== 'connected') return;

    try {
      const response = await fetchGmailEmails({ maxResults: 20 });
      setEmails(response.emails as GmailEmail[]);
    } catch (err) {
      console.error('Failed to load emails:', err);
      setError('Failed to load emails. Please try again.');
    }
  }, [fetchGmailEmails, viewState]);

  useEffect(() => {
    const initializeApp = async () => {
      const isConnected = await checkConnectionStatus();
      if (isConnected) {
        await loadEmails();
      }
    };

    initializeApp();
  }, [checkConnectionStatus, loadEmails]);

  const handleConnect = useCallback(async () => {
    setViewState('loading');
    setError(null);

    try {
      const response = await connectGmailAccount({
        redirectUrl: `${window.location.origin}/email-agent`,
      });

      if (response.authUrl) {
        window.location.href = response.authUrl;
      } else {
        setError('Failed to initiate Gmail connection.');
        setViewState('login');
      }
    } catch (err) {
      console.error('Failed to connect Gmail:', err);
      setError('Failed to connect Gmail account. Please try again.');
      setViewState('login');
    }
  }, [connectGmailAccount]);

  const handleDisconnect = useCallback(async () => {
    setViewState('loading');
    setError(null);

    try {
      await disconnectGmailAccount();
      setConnection(null);
      setEmails([]);
      setSelectedEmail(null);
      setViewState('login');
      setShowProfileMenu(false);
    } catch (err) {
      console.error('Failed to disconnect Gmail:', err);
      setError('Failed to disconnect Gmail account.');
      setViewState('connected');
    }
  }, [disconnectGmailAccount]);

  const handleRetryConnection = useCallback(async () => {
    setViewState('loading');
    const isConnected = await checkConnectionStatus();
    if (isConnected) {
      await loadEmails();
    }
  }, [checkConnectionStatus, loadEmails]);

  const handleEmailClick = (email: GmailEmail) => {
    setSelectedEmail(email);
    setEmails(prev => prev.map(e => (e.id === email.id ? { ...e, isRead: true } : e)));
  };

  const getInitials = (email: string) => {
    return email.split('@')[0].substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const emailDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (emailDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
  };

  if (viewState === 'loading') {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <Typography level="h4" className="mb-2 text-black">
            Gmail Agent
          </Typography>
          <Typography level="body-md" className="text-gray-600">
            Checking Gmail connection...
          </Typography>
        </div>
      </div>
    );
  }

  if (viewState === 'error') {
    return (
      <div className="h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md w-full">
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
            <Typography level="h4" className="mb-2 text-black">
              Connection Error
            </Typography>
            <Typography level="body-md" className="text-gray-600 mb-6">
              {error || 'Unable to check Gmail connection status'}
            </Typography>
            <button
              onClick={handleRetryConnection}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (viewState === 'login') {
    return (
      <div className="h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md w-full">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={handleRetryConnection}
                className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
              >
                Try again
              </button>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail size={24} className="text-white" />
            </div>
            <Typography level="h4" className="mb-2 text-black">
              Connect Your Gmail
            </Typography>
            <Typography level="body-md" className="text-gray-600 mb-6">
              Connect your Gmail account to start managing emails with AI assistance
            </Typography>
            <button
              onClick={handleConnect}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Connect Gmail Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedEmail) {
    return (
      <div className="h-screen flex flex-col bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSelectedEmail(null)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-700" />
            </button>
            <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
              <Mail size={18} className="text-white" />
            </div>
            <h1 className="text-xl font-semibold text-black">Mail</h1>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
            >
              {getInitials(connection?.connectedEmail || '')}
            </button>

            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-4 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{connection?.connectedEmail}</p>
                  <p className="text-xs text-gray-500">Connected</p>
                </div>
                <div className="p-2">
                  <button
                    onClick={handleDisconnect}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    Disconnect Account
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 p-6 overflow-auto">
          <div className="max-w-4xl mx-auto">
            <div className="border-b border-gray-200 pb-4 mb-6">
              <h2 className="text-2xl font-semibold text-black mb-2">{selectedEmail.subject}</h2>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span>{selectedEmail.from}</span>
                <span>•</span>
                <span>{formatDate(selectedEmail.date || '')}</span>
              </div>
            </div>

            <div className="prose max-w-none">
              <div
                className="text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: selectedEmail.body || selectedEmail.snippet || '',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
            <Mail size={18} className="text-white" />
          </div>
          <h1 className="text-xl font-semibold text-black">Mail</h1>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
          >
            {getInitials(connection?.connectedEmail || '')}
          </button>

          {showProfileMenu && (
            <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <div className="p-4 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{connection?.connectedEmail}</p>
                <p className="text-xs text-gray-500">Connected</p>
              </div>
              <div className="p-2">
                <button
                  onClick={handleDisconnect}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  Disconnect Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="px-6 py-2">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              ×
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {emails.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Mail size={48} className="mx-auto mb-4 text-gray-400" />
              <Typography level="h4" className="mb-2 text-gray-600">No emails</Typography>
              <Typography level="body-md" className="text-gray-500">Your inbox is empty</Typography>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {emails.map(email => (
              <div
                key={email.id}
                onClick={() => handleEmailClick(email)}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  !email.isRead ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-medium text-gray-700">
                      {getInitials(email.from || '')}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p
                        className={`text-sm ${!email.isRead ? 'font-semibold' : 'font-medium'} text-gray-900 truncate`}
                      >
                        {email.from}
                      </p>
                      <p className="text-xs text-gray-500 flex-shrink-0 ml-2">
                        {formatDate(email.date || '')}
                      </p>
                    </div>

                    <p
                      className={`text-sm ${!email.isRead ? 'font-medium' : ''} text-gray-900 mb-1 truncate`}
                    >
                      {email.subject}
                    </p>

                    <p className="text-sm text-gray-600 truncate">{email.snippet}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
